import { Sentry } from "./utils/sentry";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.tsx";
import { config } from "./config/env.ts";
import { RedirectToSignIn, SignedIn, SignedOut } from "@clerk/clerk-react";
import { ClerkProvider } from "@clerk/clerk-react";
import { AdminProvider } from "@/contexts/AdminContext.tsx";
import { AgentEditorProvider } from "./contexts/AgentEditorContext.tsx";
import { AgentFormProvider } from "./contexts/AgentFormContext.tsx";
import { TooltipProvider } from "./components/ui/tooltip.tsx";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { AdminAuthProvider } from "@/providers/admin-auth-provider.tsx";
import { PosthogProvider } from "./providers/posthog-provider.tsx";

if (!config.clerkPublishableKey) {
  throw new Error("Add your Clerk Publishable Key to the .env file");
}

const queryClient = new QueryClient();

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}
const root = createRoot(rootElement, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
});
root.render(
  <StrictMode>
    <PosthogProvider>
      <ClerkProvider publishableKey={config.clerkPublishableKey}>
        <SignedIn>
          <AdminAuthProvider>
            <QueryClientProvider client={queryClient}>
              <TooltipProvider>
                <AdminProvider>
                  <AgentEditorProvider>
                    <AgentFormProvider>
                      <App />
                      <Toaster position="bottom-center" richColors expand />
                    </AgentFormProvider>
                  </AgentEditorProvider>
                </AdminProvider>
              </TooltipProvider>
            </QueryClientProvider>
          </AdminAuthProvider>
        </SignedIn>
        <SignedOut>
          <RedirectToSignIn />
        </SignedOut>
      </ClerkProvider>
    </PosthogProvider>
  </StrictMode>,
);
